import React from 'react';
import styled from 'styled-components';
import { colors } from '../../../../globalStyles';
import { AssessmentIndexData } from '../AssessmentIndex/AssessmentIndex.types';
import { clampPercent, getTriageCategory } from './utils';
import { InsightsButton } from './components/InsightsButton';

type TriageHeaderProps = {
  index: AssessmentIndexData;
  onOpenInsights: () => void;
};

export const TriageHeader = ({ index, onOpenInsights }: TriageHeaderProps) => {
  const { persistenceAxis, wellbeingAxis } = index.triageLevel;
  const persistenceIsLow = index.persistenceIndex.score <= persistenceAxis.threshold;
  const wellbeingIsLow = index.wellbeingIndex.score <= wellbeingAxis.threshold;
  const triageCategory = getTriageCategory({ persistenceIsLow, wellbeingIsLow });

  // Quadrants mirror the triage matrix: rows are persistence (top = high),
  // columns are wellbeing (left = low). The active cell reflects the category.
  const quadrants = [
    { active: !persistenceIsLow && wellbeingIsLow },
    { active: !persistenceIsLow && !wellbeingIsLow },
    { active: persistenceIsLow && wellbeingIsLow },
    { active: persistenceIsLow && !wellbeingIsLow },
  ];

  const summaryText = `${wellbeingIsLow ? 'Low' : 'High'} Wellbeing + ${
    persistenceIsLow ? 'Low' : 'High'
  } Persistence`;

  return (
    <HeaderCard>
      <TriageIdentityDiv>
        <MiniMatrixDiv aria-label={triageCategory.label} role="img">
          {quadrants.map(({ active }, quadrantIndex) => (
            <MiniCellDiv
              key={quadrantIndex}
              $active={active}
              $activeColor={triageCategory.activeColor}
            >
              {active && <MiniDotDiv $markerColor={triageCategory.markerColor} />}
            </MiniCellDiv>
          ))}
        </MiniMatrixDiv>
        <TriageTextDiv>
          <TriageEyebrowSpan>Triage Category</TriageEyebrowSpan>
          <TriageLabelSpan $color={triageCategory.markerColor}>
            {triageCategory.label}
          </TriageLabelSpan>
        </TriageTextDiv>
      </TriageIdentityDiv>

      <IndexSummaryDiv>
        <SummaryTextSpan>{summaryText}</SummaryTextSpan>
        <BarsDiv>
          <BarLabelSpan>Wellbeing</BarLabelSpan>
          <BarTrackDiv>
            <BarFillDiv style={{ width: `${clampPercent(index.wellbeingIndex.score)}%` }} />
          </BarTrackDiv>
          <BarLabelSpan>Persistence</BarLabelSpan>
          <BarTrackDiv>
            <BarFillDiv style={{ width: `${clampPercent(index.persistenceIndex.score)}%` }} />
          </BarTrackDiv>
        </BarsDiv>
      </IndexSummaryDiv>

      <InsightsButton onOpenInsights={onOpenInsights} />
    </HeaderCard>
  );
};

const HeaderCard = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  box-sizing: border-box;
  padding: 18px 24px;
  background-color: ${colors.white};
  border: 1px solid ${colors.secondaryBorder};
  border-radius: 12px;

  @media (max-width: 959px) {
    row-gap: 16px;
  }
`;

const TriageIdentityDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const MiniMatrixDiv = styled.div`
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  border: 1.5px solid ${colors.text};
  border-radius: 6px;
  overflow: hidden;
`;

const MiniCellDiv = styled.div<{ $active: boolean; $activeColor: string }>`
  position: relative;
  background-color: ${({ $active, $activeColor }) => ($active ? $activeColor : colors.white)};
  border-right: 1.5px solid ${colors.text};
  border-bottom: 1.5px solid ${colors.text};

  &:nth-child(2n) {
    border-right: none;
  }

  &:nth-child(n + 3) {
    border-bottom: none;
  }
`;

const MiniDotDiv = styled.div<{ $markerColor: string }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: ${({ $markerColor }) => $markerColor};
  transform: translate(-50%, -50%);
`;

const TriageTextDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TriageEyebrowSpan = styled.span`
  color: ${colors.grayText};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const TriageLabelSpan = styled.span<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
`;

const IndexSummaryDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 240px;
`;

const SummaryTextSpan = styled.span`
  color: ${colors.text};
  font-size: 14px;
  font-weight: 700;
`;

const BarsDiv = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 176px);
  align-items: center;
  gap: 0 12px;
`;

const BarLabelSpan = styled.span`
  color: ${colors.text};
  font-size: 12px;
  font-weight: 600;
`;

const BarTrackDiv = styled.div`
  height: 8px;
  border-radius: 4px;
  background-color: ${colors.grey.lightBorder};
  overflow: hidden;
`;

const BarFillDiv = styled.div`
  height: 100%;
  border-radius: 4px;
  background-color: ${colors.secondary};
`;
